import React from 'react';
import { useTranslation } from 'react-i18next';

const OpenSourcePage = () => {
  const { t } = useTranslation();

  return (
    <div className="content-section">
      <h2>{t("openSource.title")}</h2>
      <div className="open-source-content">
        <section className="intro-section">
          <h3>{t("openSource.introduction.title")}</h3>
          <p>
            {t("openSource.introduction.description")}
          </p>
        </section>

        <section className="video-tutorials-section">
          <h3>{t("openSource.videoTutorials.title")}</h3>
          <p>
            {t("openSource.videoTutorials.description")}
          </p>
          <div className="video-link-container">
            <a 
              href="https://youtu.be/K60sDprVrj8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="video-playlist-link"
            >
              <span className="video-icon">▶️</span>
              <span className="video-link-text">{t("openSource.videoTutorials.watchButton")}</span>
            </a>
          </div>
          <div className="note-box">
            <p><strong>{t("openSource.videoTutorials.tipLabel")}</strong> {t("openSource.videoTutorials.tipText")}</p>
          </div>
        </section>

        <section className="getting-started-section">
          <h3>{t("openSource.gettingStarted.title")}</h3>
          
          <div className="step-container">
            <h4><span className="step-number open-source-step-number">1</span> {t("openSource.gettingStarted.step1.title")}</h4>
            <p>{t("openSource.gettingStarted.step1.description")}</p>
            <pre className="code-block">
              <code>
                git clone https://github.com/yeonholee50/AmpyFin.git
                cd AmpyFin
              </code>
            </pre>
          </div>

          <div className="step-container">
            <h4><span className="step-number open-source-step-number">2</span> {t("openSource.gettingStarted.step2.title")}</h4>
            <p>{t("openSource.gettingStarted.step2.description")}</p>
            <pre className="code-block">
              <code>
                pip install -r requirements.txt
              </code>
            </pre>
            <div className="note-box">
              <p><strong>{t("openSource.noteLabel")}</strong> {t("openSource.gettingStarted.step2.note")}</p>
              <ul>
                <li><a href="https://github.com/mrjbq7/ta-lib" target="_blank" rel="noopener noreferrer">Ta-Lib Python Original</a></li>
                <li><a href="https://github.com/cgohlke/talib-build" target="_blank" rel="noopener noreferrer">Ta-Lib Python Easy Installation for Windows</a></li>
              </ul>
            </div>
          </div>

          <div className="step-container">
            <h4><span className="step-number open-source-step-number">3</span> {t("openSource.gettingStarted.step3.title")}</h4>
            <p>{t("openSource.gettingStarted.step3.description")}</p>
            <ol>
              <li>
                {t("openSource.gettingStarted.step3.createEnvFile")}
                <pre className="code-block">
                  <code>
                    touch .env
                  </code>
                </pre>
              </li>
              <li>
                {t("openSource.gettingStarted.step3.openEnvFile")}
                <pre className="code-block">
                  <code>
                    POLYGON_API_KEY = "your_polygon_api_key"
                    FINANCIAL_PREP_API_KEY = "your_fmp_api_key"
                    API_KEY = "your_alpaca_api_key"
                    API_SECRET = "your_alpaca_secret_key"
                    BASE_URL = "https://paper-api.alpaca.markets"  # Paper trading (safe for testing)
                    # BASE_URL = "https://api.alpaca.markets"      # Live trading (uses real money)
                    WANDB_API_KEY = "your_wandb_api_key"
                    MONGO_URL = "your_mongo_connection_string"
                  </code>
                </pre>
              </li>
            </ol>
            <div className="note-box">
              <p><strong>⚠️ {t("openSource.importantLabel")}</strong> {t("openSource.gettingStarted.step3.importantNote")}</p>
            </div>
          </div>

          <div className="step-container">
            <h4><span className="step-number open-source-step-number">4</span> {t("openSource.gettingStarted.step4.title")}</h4>
            <div className="api-setup">
              <div className="api-item">
                <h5>{t("openSource.gettingStarted.step4.polygonApi")}</h5>
                <ul>
                  <li>{t("openSource.gettingStarted.step4.signUpAt")} <a href="https://polygon.io/" target="_blank" rel="noopener noreferrer">Polygon.io</a></li>
                  <li>{t("openSource.gettingStarted.step4.addApiKeyTo")} <code>config.py</code> {t("openSource.gettingStarted.step4.as")} <code>POLYGON_API_KEY</code></li>
                </ul>
              </div>
              <div className="api-item">
                <h5>{t("openSource.gettingStarted.step4.fmpApi")}</h5>
                <ul>
                  <li>{t("openSource.gettingStarted.step4.signUpAt")} <a href="https://financialmodelingprep.com/" target="_blank" rel="noopener noreferrer">Financial Modeling Prep</a></li>
                  <li>{t("openSource.gettingStarted.step4.addApiKeyTo")} <code>config.py</code> {t("openSource.gettingStarted.step4.as")} <code>FINANCIAL_PREP_API_KEY</code></li>
                </ul>
              </div>
              <div className="api-item">
                <h5>{t("openSource.gettingStarted.step4.alpacaApi")}</h5>
                <ul>
                  <li>{t("openSource.gettingStarted.step4.signUpAt")} <a href="https://alpaca.markets/" target="_blank" rel="noopener noreferrer">Alpaca</a></li>
                  <li>{t("openSource.gettingStarted.step4.addApiKeyTo")} <code>config.py</code> {t("openSource.gettingStarted.step4.as")} <code>API_KEY</code> {t("openSource.gettingStarted.step4.and")} <code>API_SECRET</code></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="step-container">
            <h4><span className="step-number open-source-step-number">5</span> {t("openSource.gettingStarted.step5.title")}</h4>
            <ol>
              <li>{t("openSource.gettingStarted.step5.signUpMongo")} <a href="https://www.mongodb.com/atlas/database" target="_blank" rel="noopener noreferrer">MongoDB Atlas</a></li>
              <li>{t("openSource.gettingStarted.step5.createDatabase")}</li>
              <li>{t("openSource.gettingStarted.step5.configureNetwork")}</li>
              <li>{t("openSource.gettingStarted.step5.replaceMongoUrl")} <code>MONGO_URL</code> {t("openSource.gettingStarted.step5.inEnvFile")}</li>
              <li>
                {t("openSource.gettingStarted.step5.runSetup")}
                <pre className="code-block">
                  <code>
                    python setup.py
                  </code>
                </pre>
              </li>
            </ol>
            <div className="note-box">
              <p><strong>{t("openSource.noteLabel")}</strong> {t("openSource.gettingStarted.step5.note")}</p>
            </div>
          </div>

          <div className="step-container">
            <h4><span className="step-number open-source-step-number">6</span> {t("openSource.gettingStarted.step6.title")}</h4>
            <p>{t("openSource.gettingStarted.step6.description")}</p>
            <ol>
              <li>
                {t("openSource.gettingStarted.step6.signUp")} <a href="https://wandb.ai/site" target="_blank" rel="noopener noreferrer">Weights & Biases</a>
              </li>
              <li>
                {t("openSource.gettingStarted.step6.installWandb")}
                <pre className="code-block">
                  <code>
                    pip install wandb
                  </code>
                </pre>
              </li>
              <li>
                {t("openSource.gettingStarted.step6.getApiKey")}
              </li>
              <li>
                {t("openSource.gettingStarted.step6.loginWandb")}
                <pre className="code-block">
                  <code>
                    wandb login 'your_wandb_api_key'
                  </code>
                </pre>
              </li>
              <li>
                {t("openSource.gettingStarted.step6.addApiKeyToEnv")}
                <pre className="code-block">
                  <code>
                    WANDB_API_KEY = "your_wandb_api_key"
                  </code>
                </pre>
              </li>
            </ol>
            <div className="note-box">
              <p><strong>{t("openSource.noteLabel")}</strong> {t("openSource.gettingStarted.step6.note")}</p>
            </div>
            <p>{t("openSource.gettingStarted.step6.detailedWalkthrough")} <a href="https://www.youtube.com/playlist?list=PL7hzGb_OBFXaB7VGaXXiJk5uq2CXQqi-O" target="_blank" rel="noopener noreferrer">{t("openSource.gettingStarted.step6.videoTutorialText")}</a>.</p>
            
            <div className="wandb-section">
              <h5>🔍 {t("openSource.gettingStarted.step6.wandbCapabilities.title")}</h5>
              <ul>
                <li><strong>{t("openSource.gettingStarted.step6.wandbCapabilities.trackExperiments.title")}</strong> - {t("openSource.gettingStarted.step6.wandbCapabilities.trackExperiments.description")}</li>
                <li><strong>{t("openSource.gettingStarted.step6.wandbCapabilities.compareModels.title")}</strong> - {t("openSource.gettingStarted.step6.wandbCapabilities.compareModels.description")}</li>
                <li><strong>{t("openSource.gettingStarted.step6.wandbCapabilities.visualizeResults.title")}</strong> - {t("openSource.gettingStarted.step6.wandbCapabilities.visualizeResults.description")}</li>
                <li><strong>{t("openSource.gettingStarted.step6.wandbCapabilities.collaborate.title")}</strong> - {t("openSource.gettingStarted.step6.wandbCapabilities.collaborate.description")}</li>
                <li><strong>{t("openSource.gettingStarted.step6.wandbCapabilities.versionControl.title")}</strong> - {t("openSource.gettingStarted.step6.wandbCapabilities.versionControl.description")}</li>
              </ul>
              <p>{t("openSource.gettingStarted.step6.exampleDashboard")}</p>
              <div className="dashboard-preview">
                <img src="/images/wandb-dashboard-preview.png" alt={t("openSource.gettingStarted.step6.dashboardAltText")} />
                <p className="image-caption">{t("openSource.gettingStarted.step6.dashboardCaption")}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="running-section">
          <h3>{t("openSource.running.title")}</h3>
          
          <div className="subsection">
            <h4>{t("openSource.running.tradingSystem.title")}</h4>
            <p>{t("openSource.running.tradingSystem.description")}</p>
            <pre className="code-block">
              <code>
                python ranking_client.py
                python trading_client.py
              </code>
            </pre>
          </div>

          <div className="subsection">
            <h4>{t("openSource.running.training.title")}</h4>
            <p>{t("openSource.running.training.updateControl")}</p>
            <pre className="code-block">
              <code>
                mode = 'train'
              </code>
            </pre>
            <p>{t("openSource.running.training.thenRun")}</p>
            <pre className="code-block">
              <code>
                python TradeSim/main.py
              </code>
            </pre>
            <div className="note-box">
              <p><strong>{t("openSource.noteLabel")}</strong> {t("openSource.running.training.note")}</p>
            </div>
            <p>{t("openSource.running.training.wandbIntegration")}</p>
            <ol>
              <li>{t("openSource.running.training.trainingMetrics")}</li>
              <li>{t("openSource.running.training.viewCurves")}</li>
              <li>{t("openSource.running.training.accessDashboard")} <code>https://wandb.ai/your-username/ampyfin</code></li>
            </ol>
            <div className="note-box">
              <p><strong>{t("openSource.tipLabel")}</strong> {t("openSource.running.training.tip")}</p>
            </div>
            <p>{t("openSource.running.training.videoTutorials")} <a href="https://www.youtube.com/playlist?list=PL7hzGb_OBFXaB7VGaXXiJk5uq2CXQqi-O" target="_blank" rel="noopener noreferrer">{t("openSource.running.training.videoTutorialsLinkText")}</a>.</p>
          </div>

          <div className="subsection">
            <h4>{t("openSource.running.testing.title")}</h4>
            <p>{t("openSource.running.testing.updateControl")}</p>
            <pre className="code-block">
              <code>
                mode = 'test'
              </code>
            </pre>
            <p>{t("openSource.running.testing.runTest")}</p>
            <pre className="code-block">
              <code>
                python TradeSim/main.py
              </code>
            </pre>
            <div className="note-box">
              <p>{t("openSource.running.testing.tearsheetNote")} <code>./tearsheet</code>.</p>
            </div>
            <p>{t("openSource.running.testing.wandbLogging")}</p>
            <ul>
              <li>{t("openSource.running.testing.performanceMetrics")}</li>
              <li>{t("openSource.running.testing.interactiveCharts")}</li>
              <li>{t("openSource.running.testing.tradeAnalysis")}</li>
              <li>{t("openSource.running.testing.compareResults")}</li>
            </ul>
          </div>

          <div className="subsection">
            <h4>{t("openSource.running.pushing.title")}</h4>
            <p>{t("openSource.running.pushing.updateControl")}</p>
            <pre className="code-block">
              <code>
                mode = 'push'
              </code>
            </pre>
            <p>{t("openSource.running.pushing.run")}</p>
            <pre className="code-block">
              <code>
                python TradeSim/main.py
              </code>
            </pre>
            <div className="note-box">
              <p><strong>{t("openSource.noteLabel")}</strong> {t("openSource.running.pushing.note")}</p>
            </div>
          </div>
        </section>

        <section className="components-section">
          <h3>{t("openSource.components.title")}</h3>
          <div className="file-structure">
            <h4>📂 {t("openSource.components.fileStructure")}</h4>
            <ul className="file-list">
              <li><strong>🕹️ control.py</strong>: {t("openSource.components.controlPy")}</li>
              <li><strong>🤝 trading_client.py</strong>: {t("openSource.components.tradingClientPy")}</li>
              <li><strong>🏆 ranking_client.py</strong>: {t("openSource.components.rankingClientPy")}</li>
              <li><strong>🏋️ TradeSim/main.py</strong>: {t("openSource.components.tradeSimMainPy")}</li>
              <li><strong>📜 strategies/</strong>: {t("openSource.components.strategiesDir")}</li>
              <li><strong>🔧 helper_files/</strong>: {t("openSource.components.helperFilesDir")}</li>
              <li><strong>💡 utils/</strong>: {t("openSource.components.utilsDir")}</li>
              <li><strong>📊 TradeSim/utils/logger.py</strong>: {t("openSource.components.loggerPy")}</li>
              <li><strong>🔄 TradeSim/utils/wandb_utils.py</strong>: {t("openSource.components.wandbUtilsPy")}</li>
            </ul>
          </div>
        </section>

        <section className="best-practices-section">
          <h3>{t("openSource.bestPractices.title")}</h3>
          <ul>
            <li><strong>{t("openSource.bestPractices.runRankingClient")}</strong> {t("openSource.bestPractices.beforeLiveTrading")}</li>
            <li>{t("openSource.bestPractices.alternativeApproach")} <strong>{t("openSource.bestPractices.beforeExecutingTrades")}</strong>.</li>
            <li>{t("openSource.bestPractices.preTrainedModel")}</li>
            <li><strong>{t("openSource.bestPractices.useWandb")}</strong> {t("openSource.bestPractices.trackPerformance")}</li>
            <li>{t("openSource.bestPractices.separateProjects")} <strong>{t("openSource.bestPractices.wandbProjects")}</strong> {t("openSource.bestPractices.betterOrganize")}</li>
            <li>{t("openSource.bestPractices.tagRuns")}</li>
          </ul>
        </section>

        <section className="logging-section">
          <h3>{t("openSource.logging.title")}</h3>
          <ul>
            <li><strong>system.log</strong>: {t("openSource.logging.systemLog")}</li>
            <li><strong>rank_system.log</strong>: {t("openSource.logging.rankSystemLog")}</li>
            <li><strong>{t("openSource.logging.wandbLogs.title")}</strong>: {t("openSource.logging.wandbLogs.description")}</li>
            <li><strong>{t("openSource.logging.wandbArtifacts.title")}</strong>: {t("openSource.logging.wandbArtifacts.description")}</li>
          </ul>
          <div className="note-box">
            <p><strong>{t("openSource.tipLabel")}</strong> {t("openSource.logging.tip")}</p>
          </div>
        </section>

        <section className="contributing-section">
          <h3>{t("openSource.contributing.title")}</h3>
          <p>{t("openSource.contributing.welcome")} 🎉</p>
          <ul>
            <li>{t("openSource.contributing.submitPRs")}</li>
            <li>{t("openSource.contributing.workOnTestBranch")} <strong>test branch</strong> {t("openSource.contributing.beforeMerging")} <strong>main</strong>.</li>
            <li>{t("openSource.contributing.avoidCommitting")} <strong>main branch</strong>.</li>
          </ul>
        </section>

        <section className="license-section">
          <h3>{t("openSource.license.title")}</h3>
          <p>{t("openSource.license.description")} {t("openSource.license.seeFile")} {t("openSource.license.forDetails")}</p>
        </section>
      </div>
    </div>
  );
};

export default OpenSourcePage;
